import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Flame, Copy, Check, ExternalLink, ArrowRight } from "lucide-react";

const CELL_INSTALL = `!pip install unsloth trl -q`;

const CELL_UPLOAD = `from google.colab import files
uploaded = files.upload()
# Select the qa_pairs JSON file you exported from DataForge`;

const CELL_TRAIN = `import json
from unsloth import FastLanguageModel
from datasets import Dataset
from trl import SFTTrainer
from transformers import TrainingArguments

# Load your DataForge export -- change the filename to match your upload
with open('my-crawl-export.json') as f:
    data = json.load(f)
print(f'Training on {len(data)} examples')

model, tokenizer = FastLanguageModel.from_pretrained(
    model_name='unsloth/Qwen3-4B-bnb-4bit',
    max_seq_length=2048,
    load_in_4bit=True,
)

model = FastLanguageModel.get_peft_model(
    model,
    r=16,
    target_modules=['q_proj','k_proj','v_proj','o_proj','gate_proj','up_proj','down_proj'],
    lora_alpha=32,
    lora_dropout=0,
    bias='none',
    use_gradient_checkpointing='unsloth',
    random_state=42,
)

alpaca_prompt = '''Below is an instruction that describes a task, paired with an input that provides further context. Write a response that appropriately completes the request.

### Instruction:
{}

### Input:
{}

### Response:
{}'''

def format_prompts(examples):
    texts = []
    for inst, inp, out in zip(examples['instruction'], examples['input'], examples['output']):
        text = alpaca_prompt.format(inst, inp, out) + tokenizer.eos_token
        texts.append(text)
    return {'text': texts}

dataset = Dataset.from_list(data)
dataset = dataset.map(format_prompts, batched=True)

trainer = SFTTrainer(
    model=model,
    tokenizer=tokenizer,
    train_dataset=dataset,
    dataset_text_field='text',
    max_seq_length=2048,
    args=TrainingArguments(
        per_device_train_batch_size=2,
        gradient_accumulation_steps=4,
        warmup_steps=10,
        num_train_epochs=3,
        learning_rate=2e-4,
        bf16=True,
        logging_steps=10,
        output_dir='/content/model-output',
        save_strategy='epoch',
        report_to='none',
    ),
)

trainer.train()
print('Training complete!')`;

const CELL_SAVE = `model.save_pretrained('/content/my-model-adapter')
tokenizer.save_pretrained('/content/my-model-adapter')

import shutil
shutil.make_archive('/content/my-model-adapter', 'zip', '/content/my-model-adapter')

from google.colab import files
files.download('/content/my-model-adapter.zip')`;

function CodeBlock({ code, label }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="rounded-md border border-border bg-[#0B0F18] overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-[#0E1420]">
        <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
        <Button size="sm" variant="ghost" onClick={copy} className="h-6 text-slate-400 hover:text-cyan-300">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          <span className="ml-1 font-mono text-[10px]">{copied ? "copied" : "copy"}</span>
        </Button>
      </div>
      <pre className="p-4 overflow-x-auto text-xs font-mono text-slate-300 leading-relaxed">{code}</pre>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="rounded-lg border border-border bg-[#0B0F18] p-5 space-y-3">
      <div className="flex items-center gap-3">
        <div className="h-7 w-7 rounded-full border border-cyan-500/40 bg-cyan-500/10 flex items-center justify-center font-mono text-xs text-cyan-300 shrink-0">
          {n}
        </div>
        <h3 className="font-display font-bold text-base">{title}</h3>
      </div>
      <div className="pl-10 space-y-3 text-sm text-slate-400">{children}</div>
    </div>
  );
}

export default function TrainGuide() {
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[900px]">
      <header className="space-y-2">
        <div className="flex items-center gap-2">
          <Flame className="h-5 w-5 text-cyan-400" />
          <h2 className="font-mono font-extrabold text-2xl sm:text-3xl tracking-tight uppercase">Train Your Own Model</h2>
        </div>
        <p className="text-sm text-slate-500 max-w-[640px]">
          Everything you crawl and export from DataForge can become a real, custom AI model --
          one that runs on your own machine, knows your own data, and costs nothing per query
          once it's trained.
        </p>
        <Badge variant="outline" className="font-mono text-[10px] border-emerald-500/40 text-emerald-300">
          free tools only -- no GPU required on your machine
        </Badge>
      </header>

      <Step n={1} title="Export your training data">
        <p>
          Open your finished crawl &rarr; <strong className="text-slate-300">Export</strong> &rarr;
          set <strong className="text-slate-300">Shape</strong> to <code className="text-cyan-300">qa_pairs</code> &rarr;
          <strong className="text-slate-300"> Format</strong>: <code className="text-cyan-300">json</code>.
          The more focused and high-quality your crawl (check the AI Quality Filter score), the better
          the resulting model.
        </p>
      </Step>

      <Step n={2} title="Open a free Colab notebook">
        <p>
          Go to <a href="https://colab.research.google.com" target="_blank" rel="noreferrer"
            className="text-cyan-400 hover:underline inline-flex items-center gap-1">
            colab.research.google.com <ExternalLink className="h-3 w-3" />
          </a> &rarr; New Notebook &rarr;{" "}
          <strong className="text-slate-300">Runtime &rarr; Change runtime type &rarr; T4 GPU</strong>.
          This gives you a free GPU for a few hours -- enough to train on a DataForge export.
        </p>
      </Step>

      <Step n={3} title="Install the training tools">
        <CodeBlock label="cell" code={CELL_INSTALL} />
      </Step>

      <Step n={4} title="Upload your dataset">
        <CodeBlock label="cell" code={CELL_UPLOAD} />
      </Step>

      <Step n={5} title="Train">
        <p>
          A small, capable open base model, LoRA fine-tuning (only trains a small adapter, not the
          whole model -- this is what makes it possible on a free GPU), formatted for your data:
        </p>
        <CodeBlock label="cell -- takes 30-60 min for ~500 examples over 3 epochs" code={CELL_TRAIN} />
      </Step>

      <Step n={6} title="Save and download your trained model">
        <CodeBlock label="cell" code={CELL_SAVE} />
        <p>This downloads the LoRA "adapter" -- just what changed during training, small file size.</p>
      </Step>

      <Step n={7} title="Run it locally">
        <p>Merge the adapter with the base model, convert to GGUF, then run it with a local model app:</p>
        <ol className="list-decimal list-inside space-y-1 text-slate-400">
          <li>
            We recommend <strong className="text-cyan-300">HELM-LLM</strong> -- a private, offline-capable
            desktop app built to run local models and remember what you teach it. See the{" "}
            <strong className="text-slate-300">Desktop App</strong> tab in the sidebar to get it.
          </li>
          <li>Merge + convert to GGUF (see the DataForge community docs for the current merge script)</li>
          <li>Load your model in HELM-LLM's model manager</li>
          <li>Chat with it -- fully local, free per query, and it knows what you trained it on</li>
        </ol>
      </Step>

      <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/[0.03] p-5 space-y-2">
        <h3 className="font-display font-bold text-sm text-cyan-300 flex items-center gap-2">
          <ArrowRight className="h-4 w-4" /> If results look generic or wrong
        </h3>
        <ul className="list-disc list-inside space-y-1 text-sm text-slate-400">
          <li>Add more training data -- a few hundred examples is a reasonable minimum</li>
          <li>Raise the AI Quality Filter threshold on your crawl -- garbage in, garbage out applies directly here</li>
          <li>Try <code className="text-cyan-300">num_train_epochs=5</code> instead of <code className="text-cyan-300">3</code></li>
        </ul>
      </div>

      <p className="text-xs text-slate-600 pt-2">
        A model you trained yourself, on your own data, running on your own machine, is something
        no one can take away, rate-limit, or change the pricing on. Open models are the future --
        DataForge is built to help you own that future.
      </p>
    </div>
  );
}
